import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { CarouselApi } from '@rocco/components/carousel';

import { useIslandSection } from './use-island-section';
import type { IslandMode } from './types';

const DEFAULT_AUTOPLAY_DELAY = 4000;

/** The narrowed pagination mode the autoplay hooks produce. */
type PaginationIslandMode = Extract<IslandMode, { kind: 'pagination' }>;

interface PaginationAutoplayOptions {
    /** Accessible label for the pagination control, e.g. "More from Code Interiors". */
    label: string;
    /** Dwell per slide / duration of the active dot's progress fill. Default 4000. */
    autoplayDelayMs?: number;
}

interface IslandAutoplayOptions extends PaginationAutoplayOptions {
    /** Skip island registration entirely (e.g. a single-slide carousel). */
    enabled: boolean;
    /** Higher wins when two sections cross the reading line at once. Default 1. */
    priority?: number;
}

/**
 * Owns the pagination + dwell-timer autoplay state for a carousel the caller
 * owns (it passes the embla api in via `setApi`) and returns the ready-to-render
 * pagination `IslandMode`. Same autoplay UX as the hero gallery: a dwell-timer
 * clock that survives the control unmounting on scroll, banks elapsed dwell
 * across pause/resume, stops on the last snap (the control becomes a replay
 * button), and only ticks while the control reports itself on screen (via
 * `mode.onVisible`).
 *
 * The clock lives here rather than in the island's progress fill — scrolling
 * unmounts the island (and any in-flight CSS fill with it), which would
 * otherwise drop a pending advance or reset the dwell and stick the carousel.
 * The fill is a visual indicator only; `advance` runs on the timer below.
 *
 * `total` (the dot count) tracks embla's snap count, not a tile count, so
 * multi-tile-per-view carousels get the right number of dots.
 *
 * Split out from `useIslandAutoplay` so the same state can drive either the
 * floating Magic Island (desktop) or a statically-docked control (mobile) —
 * feeding `mode` into `PaginationIslandContent` either way — without duplicating
 * the timer.
 */
export const usePaginationAutoplay = (
    emblaApi: CarouselApi | null,
    options: PaginationAutoplayOptions,
): PaginationIslandMode => {
    const autoplayDelayMs = options.autoplayDelayMs ?? DEFAULT_AUTOPLAY_DELAY;

    const [currentSlide, setCurrentSlide] = useState(0);
    const [totalSnaps, setTotalSnaps] = useState(0);
    // Whether autoplay has stopped on the last snap (so the island shows a
    // replay control). Derived from the live embla snap count `advance` uses, so
    // it can't disagree with the stop logic; settling off the last snap clears it.
    const [reachedEnd, setReachedEnd] = useState(false);
    const [isPlaying, setIsPlaying] = useState(true);
    // The clock only runs while the island is actually on screen — the island
    // reports its mount/unmount here. With banked dwell, the flicker during
    // scroll resumes rather than resets.
    const [islandActive, setIslandActive] = useState(false);
    const remainingDwellRef = useRef(autoplayDelayMs);

    useEffect(() => {
        if (!emblaApi) return;
        const onSelect = () => {
            setCurrentSlide(emblaApi.selectedScrollSnap());
            setTotalSnaps(emblaApi.scrollSnapList().length);
            if (
                emblaApi.selectedScrollSnap() <
                emblaApi.scrollSnapList().length - 1
            ) {
                setReachedEnd(false);
            }
        };
        onSelect();
        emblaApi.on('select', onSelect);
        emblaApi.on('reInit', onSelect);
        return () => {
            emblaApi.off('select', onSelect);
            emblaApi.off('reInit', onSelect);
        };
    }, [emblaApi]);

    const handleIslandVisible = useCallback(
        (visible: boolean) => setIslandActive(visible),
        [],
    );
    const togglePlay = useCallback(
        () => setIsPlaying((playing) => !playing),
        [],
    );
    const advance = useCallback(() => {
        if (!emblaApi) return;
        // Stop on the last snap instead of looping back, so the island swaps its
        // play/pause control for a replay button. Read the snap count live from
        // embla (not a captured length) so client-side navigation between
        // products can't leave a stale length that loops past the end.
        if (
            emblaApi.selectedScrollSnap() >=
            emblaApi.scrollSnapList().length - 1
        ) {
            setIsPlaying(false);
            setReachedEnd(true);
            return;
        }
        emblaApi.scrollNext();
    }, [emblaApi]);
    const replay = useCallback(() => {
        if (!emblaApi) return;
        setReachedEnd(false);
        setIsPlaying(true);
        emblaApi.scrollTo(0);
    }, [emblaApi]);

    // A fresh slide gets the full dwell. (Declared before the timer effect so its
    // reset wins over the timer's elapsed-bookkeeping on a slide change.)
    useEffect(() => {
        remainingDwellRef.current = autoplayDelayMs;
    }, [currentSlide, autoplayDelayMs]);
    // The autoplay timer: advance after the remaining dwell while playing and not
    // yet ended; on pause/unschedule, bank the elapsed so resume continues from
    // where it left off (matching the island's frozen fill).
    useEffect(() => {
        if (!emblaApi || !isPlaying || reachedEnd || !islandActive) return;
        const startedAt = performance.now();
        const timer = window.setTimeout(advance, remainingDwellRef.current);
        return () => {
            window.clearTimeout(timer);
            remainingDwellRef.current = Math.max(
                0,
                remainingDwellRef.current - (performance.now() - startedAt),
            );
        };
    }, [emblaApi, isPlaying, reachedEnd, islandActive, currentSlide, advance]);

    return useMemo<PaginationIslandMode>(
        () => ({
            kind: 'pagination',
            label: options.label,
            total: totalSnaps,
            activeIndex: currentSlide,
            onSelect: (index) => emblaApi?.scrollTo(index),
            isPlaying,
            onTogglePlay: togglePlay,
            autoplayDelayMs,
            onReplay: replay,
            atEnd: reachedEnd,
            onVisible: handleIslandVisible,
        }),
        [
            options.label,
            totalSnaps,
            currentSlide,
            emblaApi,
            isPlaying,
            togglePlay,
            autoplayDelayMs,
            replay,
            reachedEnd,
            handleIslandVisible,
        ],
    );
};

/**
 * Drives the floating Magic Island's `pagination` mode from a carousel the
 * caller owns: builds the autoplay state via `usePaginationAutoplay` and
 * registers it as a scroll-driven island section. Returns a callback ref to
 * attach to the element whose vertical extent should drive in-view.
 */
export const useIslandAutoplay = <T extends HTMLElement = HTMLElement>(
    emblaApi: CarouselApi | null,
    options: IslandAutoplayOptions,
) => {
    const mode = usePaginationAutoplay(emblaApi, options);

    return useIslandSection<T>(mode, {
        enabled: options.enabled,
        // Above link/cta default (0) so pagination sticks while peeking.
        priority: options.priority ?? 1,
    });
};
